import React, { useState, useEffect, useRef } from "react";
import ToggleSwitch from "./ToggleSwitch";

// Boxed numeric stepper matching the Android settings/hour-input design:
// a -/+ pair flanking a directly-editable field. The field accepts digits
// only and clamps to [min, max] on commit (blur / Enter).
function Stepper({ value, min = 0, max = 99, onChange }) {
  const [draft, setDraft] = useState(null);
  const maxLen = String(max).length;

  const commit = () => {
    if (draft == null) return;
    const n = parseInt(draft, 10);
    if (!Number.isNaN(n)) onChange(Math.max(min, Math.min(max, n)));
    setDraft(null);
  };

  const display = draft != null ? draft : String(value).padStart(2, "0");

  return (
    <div style={s.stepperRow}>
      <button style={s.stepBtn} onClick={() => value > min && onChange(value - 1)} aria-label="Decrease">–</button>
      <input
        style={s.stepInput}
        type="text"
        inputMode="numeric"
        value={display}
        onFocus={(e) => { setDraft(String(value)); e.target.select(); }}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, "").slice(0, maxLen))}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      />
      <button style={s.stepBtn} onClick={() => value < max && onChange(value + 1)} aria-label="Increase">+</button>
    </div>
  );
}

// "YYYY-MM-DDTHH:mm" in local time for <input type="datetime-local">.
const toLocalInputValue = (ms) => {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const TimeRecordEditDialog = ({
  initialHours,
  initialMinutes,
  initialSeconds,
  // Start timestamp (epoch ms) — editable like iOS's RecordEditSheet. Omit to
  // hide the field (older callers only edit the duration).
  initialStartedTime,
  // Human/AI tracker kind — editable like Android's TimeEditDialog AI switch.
  // The switch only renders when showAiSwitch is true (AI invoicing enabled).
  initialTrackerKind = "human",
  showAiSwitch = false,
  onUpdate,
  onClose,
}) => {
  const [hours, setHours] = useState(Number(initialHours) || 0);
  const [minutes, setMinutes] = useState(Number(initialMinutes) || 0);
  const [seconds, setSeconds] = useState(Number(initialSeconds) || 0);
  const initialStartedValue =
    initialStartedTime != null ? toLocalInputValue(initialStartedTime) : null;
  const [startedDraft, setStartedDraft] = useState(initialStartedValue);
  const [trackerKind, setTrackerKind] = useState(initialTrackerKind);

  const dialogRef = useRef(null);

  const handleUpdate = () => {
    if (!onUpdate) return;
    // Untouched (or unparseable) start field -> keep the original timestamp
    // exactly, seconds and all; datetime-local only carries minute precision.
    let startedTime = initialStartedTime;
    if (startedDraft != null && startedDraft !== initialStartedValue) {
      const parsed = new Date(startedDraft).getTime();
      if (!Number.isNaN(parsed)) startedTime = parsed;
    }
    onUpdate({ hours, minutes, seconds, startedTime, trackerKind });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        if (onClose) onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div style={s.container} ref={dialogRef}>
      {startedDraft != null && (
        <div style={s.field}>
          <label style={s.label}>Started</label>
          <input
            style={s.dateInput}
            type="datetime-local"
            value={startedDraft}
            onChange={(e) => setStartedDraft(e.target.value)}
          />
        </div>
      )}
      <div style={s.field}>
        <label style={s.label}>Hours</label>
        <Stepper value={hours} min={0} max={999} onChange={setHours} />
      </div>
      <div style={s.field}>
        <label style={s.label}>Minutes</label>
        <Stepper value={minutes} min={0} max={59} onChange={setMinutes} />
      </div>
      <div style={s.field}>
        <label style={s.label}>Seconds</label>
        <Stepper value={seconds} min={0} max={59} onChange={setSeconds} />
      </div>
      {showAiSwitch && (
        <div style={s.aiRow}>
          <label style={s.label}>AI time</label>
          <ToggleSwitch
            checked={trackerKind === "ai"}
            onChange={(on) => setTrackerKind(on ? "ai" : "human")}
            onLabel="AI"
            offLabel="Human"
          />
        </div>
      )}
      <button style={s.updateButton} onClick={handleUpdate}>
        Update
      </button>
    </div>
  );
};

const s = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    backgroundColor: "var(--modal-bg)",
    color: "var(--modal-text)",
    padding: "20px",
    borderRadius: "10px",
    zIndex: 1000,
    minWidth: "240px",
  },
  field: { display: "flex", flexDirection: "column", gap: "8px" },
  label: { fontSize: "13px", color: "var(--text-muted)" },
  stepperRow: { display: "flex", alignItems: "center", gap: "10px" },
  stepBtn: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    border: "1px solid var(--border)",
    background: "transparent",
    color: "var(--primary)",
    fontSize: "20px",
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  stepInput: {
    flex: 1,
    height: "40px",
    minWidth: 0,
    textAlign: "center",
    fontWeight: 600,
    fontSize: "16px",
    color: "var(--text)",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    outline: "none",
    boxSizing: "border-box",
  },
  dateInput: {
    height: "40px",
    padding: "0 10px",
    fontWeight: 600,
    fontSize: "14px",
    color: "var(--text)",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    outline: "none",
    boxSizing: "border-box",
    colorScheme: "light dark", // native picker follows the app theme
  },
  aiRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  updateButton: {
    backgroundColor: "var(--primary)",
    color: "white",
    padding: "12px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    fontWeight: 700,
    marginTop: "4px",
  },
};

export default TimeRecordEditDialog;
